import React, { createContext, useContext, useState, useMemo, ReactNode } from 'react';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import {
  getCachedStringsItems,
  updateTranslationLine,
  createTranslationLine,
} from '@/lib/stores/stringsStore';
import type { StringsItem } from '@/lib/stores/stringsStore';

interface DisplayLanguageContextType {
  /** The currently selected display language code, or null for source text */
  displayLanguage: string | null;
  /** Set the display language (null = source text) */
  setDisplayLanguage: (language: string | null) => void;
  /** Available languages from game settings (empty if none configured) */
  availableLanguages: string[];
  /** Whether the language selector should be visible (multiple languages configured) */
  hasMultipleLanguages: boolean;
}

const DisplayLanguageContext = createContext<DisplayLanguageContextType | undefined>(undefined);

interface DisplayLanguageProviderProps {
  children: ReactNode;
}

export function DisplayLanguageProvider({ children }: DisplayLanguageProviderProps) {
  const { selectedGame } = useSimplifiedUserData();
  const [displayLanguage, setDisplayLanguage] = useState<string | null>(null);

  const defaultLanguage = selectedGame?.settings?.defaultLanguage || null;

  const availableLanguages = useMemo(() => {
    const languages = selectedGame?.settings?.languages;
    if (!languages || !Array.isArray(languages)) return [];
    return languages
      .map((lang: string | { code: string; name?: string }) =>
        typeof lang === 'string' ? lang : lang.code
      )
      .filter((code) => code !== defaultLanguage); // exclude default language — it's already the source
  }, [selectedGame?.settings?.languages, defaultLanguage]);

  const hasMultipleLanguages = availableLanguages.length > 0;

  // Reset display language if the selected language is no longer available
  const safeDisplayLanguage = useMemo(() => {
    if (displayLanguage && !availableLanguages.includes(displayLanguage)) {
      return null;
    }
    return displayLanguage;
  }, [displayLanguage, availableLanguages]);

  return (
    <DisplayLanguageContext.Provider
      value={{
        displayLanguage: safeDisplayLanguage,
        setDisplayLanguage,
        availableLanguages,
        hasMultipleLanguages,
      }}
    >
      {children}
    </DisplayLanguageContext.Provider>
  );
}

/**
 * Hook to access the display language context.
 * Throws if used outside a DisplayLanguageProvider.
 */
export function useDisplayLanguage() {
  const context = useContext(DisplayLanguageContext);
  if (context === undefined) {
    throw new Error('useDisplayLanguage must be used within a DisplayLanguageProvider');
  }
  return context;
}

/**
 * Optional hook that returns null if not inside a DisplayLanguageProvider.
 * Useful for components that may or may not have the context available.
 */
export function useDisplayLanguageOptional() {
  return useContext(DisplayLanguageContext);
}

/**
 * Resolve the text to display for a TMS string item based on the current display language.
 * 
 * - If displayLanguage is null → returns source_text (default behavior)
 * - If displayLanguage is set and translation exists → returns translated_text
 * - If displayLanguage is set but no translation → returns noTranslationLabel
 * 
 * @param stringItem       The TMS string item
 * @param displayLanguage  Selected language code, or null for source text
 * @param noTranslationLabel  Translated label for missing translations
 *                            (from usePlatformTranslation: t('platform.TMS.no_translation'))
 */
export function resolveStringDisplayText(
  stringItem: StringsItem,
  displayLanguage: string | null,
  noTranslationLabel: string = 'no translation available'
): { text: string; isMissing: boolean } {
  if (!displayLanguage) {
    return { text: stringItem.source_text, isMissing: false };
  }

  const translation = stringItem.translations?.find(
    (t) => t.language_code === displayLanguage
  );

  if (translation?.translated_text) {
    return { text: translation.translated_text, isMissing: false };
  }

  return { text: noTranslationLabel, isMissing: true };
}

/**
 * Resolve the display text for a dialog field based on the current display language.
 * 
 * This is a convenience function that looks up the TMS string for a field
 * and resolves the appropriate text (source, translation, or fallback).
 * 
 * Use this in views that display field text but don't use the full BaseFieldEditor
 * (e.g., flow nodes, expanded section nodes).
 * 
 * @param field             The dialog field object (must have text, optionally string_id and translatable)
 * @param displayLanguage   Selected language code, or null for source text
 * @param gameId            The game ID for looking up cached strings
 * @param noTranslationLabel  Label for missing translations (default: 'no translation')
 * @returns The resolved text to display
 */
export function resolveFieldDisplayText(
  field: { text: string; string_id?: string | null; translatable?: boolean },
  displayLanguage: string | null,
  gameId?: string,
  noTranslationLabel: string = 'no translation'
): string {
  // If no display language, field not translatable, no TMS link, or no game → return source text
  if (!displayLanguage || !field.translatable || !field.string_id || !gameId) {
    return field.text;
  }

  const strings = getCachedStringsItems(gameId);
  const linkedString = strings.find(s => s.id === field.string_id);
  if (!linkedString) {
    return field.text;
  }

  const resolved = resolveStringDisplayText(linkedString, displayLanguage, noTranslationLabel);
  return resolved.text || field.text;
}

/**
 * Resolve the display text for a dialog option based on the current display language.
 * 
 * This is a convenience function that looks up the TMS string for an option
 * and resolves the appropriate text (source, translation, or fallback).
 * 
 * Use this in views that display option text but don't use the full DialogOptionEditor
 * (e.g., flow nodes, write view, play display).
 * 
 * @param option            The dialog option object (must have option_text, optionally string_id)
 * @param displayLanguage   Selected language code, or null for source text
 * @param gameId            The game ID for looking up cached strings
 * @param noTranslationLabel  Label for missing translations (default: 'no translation')
 * @returns The resolved text to display
 */
export function resolveOptionDisplayText(
  option: { option_text: string; string_id?: string | null },
  displayLanguage: string | null,
  gameId?: string,
  noTranslationLabel: string = 'no translation'
): string {
  // If no display language selected or option has no TMS link, return source text
  if (!displayLanguage || !option.string_id || !gameId) {
    return option.option_text;
  }

  const strings = getCachedStringsItems(gameId);
  const linkedString = strings.find(s => s.id === option.string_id);
  if (!linkedString) {
    return option.option_text;
  }

  const resolved = resolveStringDisplayText(linkedString, displayLanguage, noTranslationLabel);
  return resolved.text || option.option_text;
}

/**
 * Resolve the editable text for a dialog field when entering inline edit mode.
 *
 * Unlike resolveFieldDisplayText (which shows a "no translation" label),
 * this returns an empty string when no translation exists yet, making it
 * suitable for initialising a text input / textarea.
 *
 * - displayLanguage is null → returns field.text (source)
 * - displayLanguage is set, translation exists → returns translated text
 * - displayLanguage is set, no translation → returns '' (empty)
 */
export function resolveFieldEditableText(
  field: { text: string; string_id?: string | null; translatable?: boolean },
  displayLanguage: string | null,
  gameId?: string,
): string {
  if (!displayLanguage || !field.translatable || !field.string_id || !gameId) {
    return field.text;
  }

  const strings = getCachedStringsItems(gameId);
  const linkedString = strings.find(s => s.id === field.string_id);
  if (!linkedString) return field.text;

  const translation = linkedString.translations?.find(
    (t) => t.language_code === displayLanguage
  );
  return translation?.translated_text ?? '';
}

/**
 * Resolve the editable text for a dialog option when entering inline edit mode.
 *
 * Same semantics as resolveFieldEditableText but for option objects.
 */
export function resolveOptionEditableText(
  option: { option_text: string; string_id?: string | null },
  displayLanguage: string | null,
  gameId?: string,
): string {
  if (!displayLanguage || !option.string_id || !gameId) {
    return option.option_text;
  }

  const strings = getCachedStringsItems(gameId);
  const linkedString = strings.find(s => s.id === option.string_id);
  if (!linkedString) return option.option_text;

  const translation = linkedString.translations?.find(
    (t) => t.language_code === displayLanguage
  );
  return translation?.translated_text ?? '';
}

/**
 * Save a translation for a TMS-linked string from an inline flow-node editor.
 *
 * Looks up the existing translation to decide whether to create or update.
 * Safe to call without tracking refs — suitable for one-shot saves from
 * flow-node inline editors (as opposed to the debounced saves in field editors).
 */
export async function saveFieldTranslation(
  stringId: string,
  languageCode: string,
  text: string,
  gameId: string,
): Promise<void> {
  try {
    const strings = getCachedStringsItems(gameId);
    const linkedString = strings.find(s => s.id === stringId);

    const existingTranslation = linkedString?.translations?.find(
      (t) => t.language_code === languageCode
    );

    if (existingTranslation?.id) {
      await updateTranslationLine(existingTranslation.id, {
        language_code: languageCode,
        translated_text: text,
        approved: false,
      });
    } else {
      await createTranslationLine({
        dialogue_line_id: stringId,
        language_code: languageCode,
        translated_text: text,
        approved: false,
      });
    }
  } catch (error) {
    console.error('[saveFieldTranslation] Failed to save translation:', error);
  }
}
